import type { TFunction } from "i18next";
import { toast } from "sonner";
import { OfflineWriteBlockedError } from "utils/apiClient";

const API_ERROR_I18N_KEYS: Record<string, string> = {
    "Insufficient balance": "errors.insufficientBalance",
    "Pool not found for this account and currency": "errors.poolScopeMismatch",
    "Pool not found": "errors.poolNotFound",
    "Transaction not found": "errors.transactionNotFound",
};

function extractRawMessage(error: unknown): string {
    if (error instanceof OfflineWriteBlockedError) return "OFFLINE_WRITE_BLOCKED";
    if (typeof error === "string") return error.trim();
    if (error instanceof Error) return error.message.trim();
    return "";
}

function isLikelyNetworkFailure(message: string): boolean {
    return /Failed to fetch|Load failed|NetworkError|network error/i.test(message);
}

function isTechnicalApiMessage(message: string): boolean {
    return /^(GET|POST|PUT|PATCH|DELETE) \//.test(message)
        || /failed with status \d+/.test(message);
}

export function resolveApiErrorMessage(
    error: unknown,
    t: TFunction,
    fallbackKey: string,
): { title: string; description?: string } {
    const raw = extractRawMessage(error);

    if (!raw) {
        return { title: t(fallbackKey) };
    }

    if (raw === "OFFLINE_WRITE_BLOCKED") {
        return { title: t("offline.disabled.title") };
    }

    if (isLikelyNetworkFailure(raw)) {
        return { title: t("errors.network") };
    }

    const i18nKey = API_ERROR_I18N_KEYS[raw];
    if (i18nKey) {
        return { title: t(i18nKey) };
    }

    if (!isTechnicalApiMessage(raw)) {
        return { title: raw };
    }

    const fallback = t(fallbackKey);
    return {
        title: fallback,
        description: raw !== fallback ? raw : undefined,
    };
}

export function toastApiError(error: unknown, t: TFunction, fallbackKey: string): void {
    const { title, description } = resolveApiErrorMessage(error, t, fallbackKey);
    if (description) {
        toast.error(title, { description });
    } else {
        toast.error(title);
    }
}
